import { useEffect, useMemo, useRef, useState } from "react";
import type {
  ReviewComment,
  ReviewCommentDomain,
  ReviewCommentThread,
} from "../../models/ReviewComment";
import { buildReviewCommentThreads } from "../../models/ReviewComment";
import { useReviewCommentStore } from "../../store/useReviewCommentStore";
import { useUserStore } from "../../store/userStore";

type ReviewCommentsPanelProps = {
  domain: ReviewCommentDomain;
  masterAssemblyId: number;
  manualId?: number;
  sectionId?: string | null;
  blockId?: string | null;
  figId?: number;
  itemNumber?: number;
  autoStartAddToken?: number;
};

const hasNumeric = (value: number | undefined | null): value is number =>
  value != null && Number.isFinite(value);

const hasString = (value: string | undefined | null): value is string =>
  !!value && value.trim().length > 0;

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

function matchCommentByPriority(
  comment: ReviewComment,
  context: {
    domain: ReviewCommentDomain;
    masterAssemblyId: number;
    manualId?: number;
    sectionId?: string | null;
    blockId?: string | null;
    figId?: number;
    itemNumber?: number;
  }
): boolean {
  if (comment.domain !== context.domain) return false;
  if (comment.masterAssemblyId !== context.masterAssemblyId) return false;

  if (hasString(context.blockId)) {
    return (
      comment.blockId === context.blockId &&
      (!hasNumeric(context.manualId) || comment.manualId === context.manualId)
    );
  }

  if (hasNumeric(context.itemNumber)) {
    return (
      comment.itemNumber === context.itemNumber &&
      (!hasNumeric(context.figId) || comment.figId === context.figId)
    );
  }

  if (hasNumeric(context.figId)) {
    return comment.figId === context.figId;
  }

  if (hasString(context.sectionId)) {
    return (
      comment.sectionId === context.sectionId &&
      (!hasNumeric(context.manualId) || comment.manualId === context.manualId)
    );
  }

  if (hasNumeric(context.manualId)) {
    return comment.manualId === context.manualId;
  }

  return true;
}

export default function ReviewCommentsPanel({
  domain,
  masterAssemblyId,
  manualId,
  sectionId,
  blockId,
  figId,
  itemNumber,
  autoStartAddToken,
}: ReviewCommentsPanelProps) {
  const user = useUserStore((s) => s.user);
  const comments = useReviewCommentStore((s) => s.comments);
  const isLoading = useReviewCommentStore((s) => s.isLoading);
  const isSaving = useReviewCommentStore((s) => s.isSaving);
  const error = useReviewCommentStore((s) => s.error);
  const selectedThreadId = useReviewCommentStore((s) => s.selectedThreadId);
  const setContext = useReviewCommentStore((s) => s.setContext);
  const loadComments = useReviewCommentStore((s) => s.loadComments);
  const addRootComment = useReviewCommentStore((s) => s.addRootComment);
  const addReply = useReviewCommentStore((s) => s.addReply);
  const resolveThread = useReviewCommentStore((s) => s.resolveThread);
  const reopenThread = useReviewCommentStore((s) => s.reopenThread);
  const selectThread = useReviewCommentStore((s) => s.selectThread);

  const [newComment, setNewComment] = useState("");
  const [showAddComposer, setShowAddComposer] = useState(false);
  const [openReplyThreadId, setOpenReplyThreadId] = useState<string | null>(
    null
  );
  const [replyDraftByThread, setReplyDraftByThread] = useState<
    Record<string, string>
  >({});

  const addTextAreaRef = useRef<HTMLTextAreaElement>(null);

  const basePath = process.env.REACT_APP_BASE_NAME || "";
  const oemFromBase = basePath.split("/")[1] || "";
  const oemFromUser = user?.oemId ? `Oem${user.oemId}` : "";
  const oem = oemFromBase || oemFromUser || "Oem1";

  useEffect(() => {
    if (!hasNumeric(masterAssemblyId) || masterAssemblyId <= 0) return;
    setContext(oem, masterAssemblyId);
    void loadComments();
  }, [loadComments, masterAssemblyId, oem, setContext]);

  useEffect(() => {
    if (!autoStartAddToken) return;
    setShowAddComposer(true);
    window.requestAnimationFrame(() => addTextAreaRef.current?.focus());
  }, [autoStartAddToken]);

  const allThreads = useMemo(
    () => buildReviewCommentThreads(comments),
    [comments]
  );

  const filteredThreads = useMemo(() => {
    return allThreads.filter((thread) =>
      matchCommentByPriority(thread.rootComment, {
        domain,
        masterAssemblyId,
        manualId,
        sectionId,
        blockId,
        figId,
        itemNumber,
      })
    );
  }, [
    allThreads,
    blockId,
    domain,
    figId,
    itemNumber,
    manualId,
    masterAssemblyId,
    sectionId,
  ]);

  const effectiveManualId = hasNumeric(manualId) ? manualId : undefined;
  const effectiveFigId = hasNumeric(figId) ? figId : undefined;
  const effectiveItemNumber = hasNumeric(itemNumber) ? itemNumber : undefined;
  const effectiveSectionId = hasString(sectionId) ? sectionId : undefined;
  const effectiveBlockId = hasString(blockId) ? blockId : undefined;
  const userId =
    user?.userId && Number(user.userId) > 0
      ? String(user.userId)
      : (user?.emailId || user?.username || "").trim();
  const userName = user?.username || user?.emailId || "Unknown User";

  const handleAddRootComment = async () => {
    const message = newComment.trim();
    if (!message || !userId) return;

    await addRootComment({
      masterAssemblyId,
      domain,
      manualId: effectiveManualId ?? null,
      sectionId: effectiveSectionId ?? null,
      blockId: effectiveBlockId ?? null,
      figId: effectiveFigId ?? null,
      itemNumber: effectiveItemNumber ?? null,
      userId,
      userName,
      message,
    });

    setNewComment("");
    setShowAddComposer(false);
  };

  const handleReply = async (thread: ReviewCommentThread) => {
    const message = (replyDraftByThread[thread.threadId] || "").trim();
    if (!message || !userId) return;

    await addReply({
      parentCommentId: thread.rootComment.commentId,
      sectionId: thread.rootComment.sectionId ?? null,
      blockId: thread.rootComment.blockId ?? null,
      figId: thread.rootComment.figId ?? null,
      itemNumber: thread.rootComment.itemNumber ?? null,
      userId,
      userName,
      message,
    });

    setReplyDraftByThread((state) => ({ ...state, [thread.threadId]: "" }));
    setOpenReplyThreadId(null);
  };

  const contextLabel = effectiveBlockId
    ? `Block: ${effectiveBlockId}`
    : effectiveItemNumber != null
    ? `Item: ${effectiveItemNumber}`
    : effectiveFigId != null
    ? `Figure: ${effectiveFigId}`
    : effectiveSectionId
    ? `Section: ${effectiveSectionId}`
    : effectiveManualId != null
    ? `Manual: ${effectiveManualId}`
    : "Current Assembly";

  return (
    <div className="review-comments-panel">
      <div className="review-comments-panel__header">
        <div className="review-comments-panel__title">Review Comments</div>
        <div className="review-comments-panel__context">{contextLabel}</div>
      </div>

      {effectiveBlockId ? (
        <div className="review-comments-panel__toolbar">
          <button
            className="btn btn-sm btn-outline-primary"
            type="button"
            onClick={() => {
              setShowAddComposer((value) => !value);
              setNewComment("");
            }}
          >
            Add Comment
          </button>
        </div>
      ) : null}

      {showAddComposer ? (
        <div className="review-comments-composer">
          <textarea
            ref={addTextAreaRef}
            className="form-control form-control-sm"
            rows={3}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write a new root comment..."
          />
          <div className="review-comments-composer__actions">
            <button
              className="btn btn-sm btn-primary"
              type="button"
              disabled={!newComment.trim() || isSaving}
              onClick={() => void handleAddRootComment()}
            >
              Add
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              type="button"
              onClick={() => {
                setShowAddComposer(false);
                setNewComment("");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {error ? <div className="alert alert-danger py-2 mt-2">{error}</div> : null}

      <div className="review-thread-list">
        {isLoading ? (
          <div className="text-muted small">Loading comments...</div>
        ) : filteredThreads.length === 0 ? (
          <div className="text-muted small">No comments for this context.</div>
        ) : (
          filteredThreads.map((thread) => {
            const isOpen = thread.rootComment.status === "open";
            const isSelected = selectedThreadId === thread.threadId;

            return (
              <div
                key={thread.threadId}
                className={`review-thread ${isSelected ? "review-thread--selected" : ""}`}
                onClick={() => selectThread(thread.threadId)}
              >
                <div className="review-thread__root">
                  <div className="review-thread__meta">
                    <span className="review-thread__author">
                      {thread.rootComment.userName}
                    </span>
                    <span className="review-thread__date">
                      {formatDateTime(thread.rootComment.createdAt)}
                    </span>
                    <span
                      className={`review-thread__status ${
                        isOpen ? "open" : "resolved"
                      }`}
                    >
                      {isOpen ? "OPEN" : "RESOLVED"}
                    </span>
                  </div>

                  <div className="review-thread__message">
                    {thread.rootComment.message}
                  </div>

                  <div className="review-thread__actions">
                    <button
                      className="btn btn-sm btn-link px-0"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenReplyThreadId((current) =>
                          current === thread.threadId ? null : thread.threadId
                        );
                      }}
                    >
                      Reply
                    </button>
                    {isOpen ? (
                      <button
                        className="btn btn-sm btn-link px-0"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void resolveThread(thread.threadId);
                        }}
                      >
                        Resolve
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-link px-0"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void reopenThread(thread.threadId);
                        }}
                      >
                        Reopen
                      </button>
                    )}
                  </div>
                </div>

                {thread.replies.length > 0 ? (
                  <div className="review-thread__replies">
                    {thread.replies.map((reply) => (
                      <div key={reply.commentId} className="review-reply">
                        <div className="review-thread__meta">
                          <span className="review-thread__author">
                            {reply.userName}
                          </span>
                          <span className="review-thread__date">
                            {formatDateTime(reply.createdAt)}
                          </span>
                        </div>
                        <div className="review-thread__message">{reply.message}</div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {openReplyThreadId === thread.threadId ? (
                  <div
                    className="review-thread__reply-editor"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <textarea
                      className="form-control form-control-sm"
                      rows={2}
                      value={replyDraftByThread[thread.threadId] || ""}
                      onChange={(e) =>
                        setReplyDraftByThread((state) => ({
                          ...state,
                          [thread.threadId]: e.target.value,
                        }))
                      }
                      placeholder="Write a reply..."
                    />
                    <div className="review-comments-composer__actions">
                      <button
                        className="btn btn-sm btn-primary"
                        type="button"
                        disabled={isSaving}
                        onClick={() => void handleReply(thread)}
                      >
                        Post Reply
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        type="button"
                        onClick={() => setOpenReplyThreadId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
